'use client';

import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { X, ArrowRight, Dog } from 'lucide-react';
import { toast } from 'sonner';

interface RequestFormData {
  ownerName: string;
  email: string;
  phone: string;
  dogName: string;
  dogAge: string;
  dogBreed: string;
  vetName: string;
  vetPractice: string;
  eligibilityConfirmed: boolean;
  message: string;
}

export default function RequestToJoinModal({
  trial,
  onClose,
}: {
  trial: { code: string; name: string; eligibility: { id: string; text: string }[] };
  onClose: () => void;
}) {
  const [isLoading, setIsLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const { register, handleSubmit, formState: { errors } } = useForm<RequestFormData>();

  const onSubmit = async (data: RequestFormData) => {
    setIsLoading(true);
    // BACKEND INTEGRATION: POST /api/trials/${trial.code}/requests with form data
    await new Promise((r) => setTimeout(r, 1500));
    setSubmitted(true);
    setIsLoading(false);
    toast.success('Your request has been submitted. A Clinigol vet will be in touch shortly.');
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-foreground/30 backdrop-blur-sm" onClick={onClose} />
      <div className="relative card-base w-full max-w-xl shadow-card-lg fade-in max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-card z-10 px-6 pt-6 pb-4 border-b border-border">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <span className="text-xs font-mono font-bold text-primary bg-primary/10 px-2 py-0.5 rounded">{trial.code}</span>
              </div>
              <h3 className="text-base font-semibold text-foreground">Request to join {trial.name}</h3>
            </div>
            <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {submitted ? (
          <div className="p-8 text-center">
            <div className="w-14 h-14 rounded-2xl bg-positive-bg flex items-center justify-center mx-auto mb-4 score-reveal">
              <Dog className="w-7 h-7 text-positive" />
            </div>
            <h3 className="text-lg font-bold text-foreground mb-2">Request submitted!</h3>
            <p className="text-sm text-muted-foreground leading-relaxed mb-6">
              Thank you for expressing interest in the {trial.name} trial. A Clinigol-affiliated vet will review your request and be in touch within 3 working days.
            </p>
            <button onClick={onClose} className="btn-primary">Close</button>
          </div>
        ) : (
          <form onSubmit={handleSubmit(onSubmit)} className="p-6 space-y-5">
            <div>
              <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-widest mb-3">About you</h4>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1.5">Your full name</label>
                  <input type="text" className="input-field" placeholder="Eleanor Hartley" {...register('ownerName', { required: 'Name is required' })} />
                  {errors.ownerName && <p className="text-xs text-negative mt-1">{errors.ownerName.message}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1.5">Email address</label>
                  <input type="email" className="input-field" placeholder="eleanor@email.com" {...register('email', { required: 'Email is required' })} />
                  {errors.email && <p className="text-xs text-negative mt-1">{errors.email.message}</p>}
                </div>
              </div>
              <div className="mt-3">
                <label className="block text-sm font-medium text-foreground mb-1.5">Phone number <span className="text-muted-foreground font-normal">(optional)</span></label>
                <input type="tel" className="input-field" placeholder="+44 7700 900000" {...register('phone')} />
              </div>
            </div>

            <div>
              <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-widest mb-3">About your dog</h4>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1.5">Dog&apos;s name</label>
                  <input type="text" className="input-field" placeholder="Barley" {...register('dogName', { required: 'Required' })} />
                  {errors.dogName && <p className="text-xs text-negative mt-1">{errors.dogName.message}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1.5">Age</label>
                  <input type="text" className="input-field" placeholder="11 years" {...register('dogAge', { required: 'Required' })} />
                  {errors.dogAge && <p className="text-xs text-negative mt-1">{errors.dogAge.message}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1.5">Breed</label>
                  <input type="text" className="input-field" placeholder="Border Collie" {...register('dogBreed', { required: 'Required' })} />
                  {errors.dogBreed && <p className="text-xs text-negative mt-1">{errors.dogBreed.message}</p>}
                </div>
              </div>
            </div>

            <div>
              <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-widest mb-3">Your vet</h4>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1.5">Vet&apos;s name</label>
                  <input type="text" className="input-field" placeholder="Dr. Sarah Jones" {...register('vetName', { required: 'Vet name is required' })} />
                  {errors.vetName && <p className="text-xs text-negative mt-1">{errors.vetName.message}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1.5">Practice name</label>
                  <input type="text" className="input-field" placeholder="Greenview Vets" {...register('vetPractice', { required: 'Practice name is required' })} />
                  {errors.vetPractice && <p className="text-xs text-negative mt-1">{errors.vetPractice.message}</p>}
                </div>
              </div>
            </div>

            {/* Eligibility confirmation */}
            <div className="bg-muted/40 rounded-xl p-4">
              <p className="text-xs font-semibold text-foreground mb-3">Please confirm your dog meets the eligibility criteria:</p>
              <ul className="space-y-2 mb-3">
                {trial.eligibility.map((item) => (
                  <li key={item.id} className="text-xs text-muted-foreground flex items-start gap-2">
                    <span className="text-primary mt-0.5">•</span>
                    {item.text}
                  </li>
                ))}
              </ul>
              <div className="flex items-start gap-2">
                <input
                  type="checkbox"
                  id="eligibilityConfirmed"
                  className="w-4 h-4 mt-0.5 rounded border-input accent-primary"
                  {...register('eligibilityConfirmed', { required: 'Please confirm eligibility before submitting' })}
                />
                <label htmlFor="eligibilityConfirmed" className="text-sm text-foreground">
                  I confirm my dog meets the eligibility criteria above
                </label>
              </div>
              {errors.eligibilityConfirmed && <p className="text-xs text-negative mt-1">{errors.eligibilityConfirmed.message}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">Anything else you&apos;d like us to know? <span className="text-muted-foreground font-normal">(optional)</span></label>
              <textarea className="input-field resize-none" rows={3} placeholder="Tell us a little about what you&apos;ve noticed with your dog..." {...register('message')} />
            </div>

            <div className="flex gap-3 pt-1">
              <button type="button" onClick={onClose} className="btn-secondary flex-1">Cancel</button>
              <button type="submit" disabled={isLoading} className="btn-primary flex-1 flex items-center justify-center gap-2">
                {isLoading ? (
                  <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  <>Submit Request <ArrowRight className="w-4 h-4" /></>
                )}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}